import { FC } from 'react';

import { GRADIENT_INPUT_STYLE } from 'views/OfferDesign/OfferDesign.consts';
import { getFieldError } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

import {
  FONT_WEIGHT_OPTIONS,
  POPUP_DEFAULTS,
  TEXT_FIELDS,
  UPLOAD_FIELDS
} from './PopupFormFields.consts';
import { PopupFormFieldsProps } from './PopupFormFields.types';

export const PopupFormFields: FC<PopupFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique
}) => {
  const uniqueFlags = { isNameUnique, isExternalIdUnique };
  const selectedWeight =
    FONT_WEIGHT_OPTIONS.find((o) => o.value === values.fontWeight) || FONT_WEIGHT_OPTIONS[0];
  const bgColorValue =
    typeof values.backgroundColor === 'object' && values.backgroundColor
      ? values.backgroundColor
      : POPUP_DEFAULTS.backgroundColor;

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Popup</h3>

      <div className="flex gap-5 mt-4">
        {TEXT_FIELDS.map(({ label, name, placeholder, defaultValue, uniqueFlag }) => {
          const error = getFieldError(name, touched, errors, uniqueFlags, uniqueFlag);
          return (
            <Input
              key={name}
              label={label}
              isRequired
              name={name}
              placeholder={placeholder}
              value={values[name] ?? defaultValue}
              onChange={handleChange}
              onBlur={handleBlur}
              state={error ? 'error' : 'default'}
              inputClassName={error ? 'border-base-destructive' : ''}
              description={error}
              className="flex-1"
            />
          );
        })}
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">Popup Design</h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the popup to fit your store theme.
      </p>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className={`w-1/2 ${GRADIENT_INPUT_STYLE}`}>
        <AcGradientInput
          header="Background Color"
          name="backgroundColor"
          defaultValue={bgColorValue}
          onChange={handleChange}
          setValue={setFieldValue}
          onBlur={handleBlur}
        />
      </div>

      {UPLOAD_FIELDS.map(({ title, fieldName, forceType, tooltip, isRequired }) => (
        <div key={fieldName} className="mt-5">
          <UploadImage
            title={title}
            className="w-full !flex flex-row px-5"
            onUpload={(url) => setFieldValue(fieldName, url)}
            selectedImageUrl={values[fieldName] as string}
            forceType={forceType}
            isRequired={isRequired}
            TooltipContent={() => <span>{tooltip}</span>}
          />
        </div>
      ))}

      <Separator className="mt-9 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">Border</h3>

      <div className="flex gap-5 mt-4">
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Fill Color"
            name="borderColor"
            defaultValue={
              typeof values.borderColor === 'object' && values.borderColor
                ? values.borderColor
                : POPUP_DEFAULTS.borderColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Width (in px)"
            name="borderWidth"
            type="number"
            min={0}
            value={Number(values.borderWidth)}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      </div>

      <Separator className="mt-9 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">Title</h3>

      <div className="mt-4">
        <Input
          label="Title Name"
          name="title"
          placeholder="Title"
          value={values.title || ''}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>

      <div className="flex gap-5 mt-4">
        <div className="flex-1">
          <ColorPicker
            label="Color"
            defaultValue={values.fontColor?.colorOne || POPUP_DEFAULTS.fontColor.colorOne}
            onChange={(color) => setFieldValue('fontColor', { colorOne: color })}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Size"
            name="fontSize"
            type="number"
            value={values.fontSize}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex-1">
          <InputSelectDropdown
            label="Weight"
            options={FONT_WEIGHT_OPTIONS}
            defaultSelected={selectedWeight}
            onOptionChange={(value) => setFieldValue('fontWeight', value)}
          />
        </div>
      </div>
    </div>
  );
};
